import './off-rent.scss';
import { useContext, useEffect, useState } from 'react';
import { Button } from '../../../../components/form/button';
import { v4 as uuidv4 } from 'uuid';
import {
  InventoryReturnRequest,
  OffRentRequest,
  Order,
  OrderProduct,
  OrderReturnStatus,
  OrderType,
} from '../../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../../clients/quipli-service/order/order-api-v1';
import { toast } from '../../../../util/toasts';
import { Toggle } from '../../../../components/form/toggle';
import { Dictionary, isEqual } from 'lodash';
import '../status-transitions.scss';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import Dinero from 'dinero.js';
import { TenantContext } from '../../../../context/tenant-context';
import { InventoryReturnHourInput, InventoryReturnLocation, QuantityOffRentInput } from '../inventory-return';
import { Location } from '../../../../clients/quipli-service/location/location';
import { RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import { ProductTrackingType, ProductType } from '../../../../clients/quipli-service/product/product';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import {
  orderToCalculateFinancialSummaryRequest,
  toPartialOffRentRequest,
} from '../shared/utils/partial-offrent-utils';
import { Spinner } from '../../../../components/spinner/spinner';
import { FuelCharge } from '../shared/types/fuel-charge-types';
import { InventoryReturnFuelChargeInput } from '../shared/components/inventory-return-fuel-charge-input';
import { useHourOverageManagement } from '../shared/hooks/use-hour-overage-management';

export type PartialOffRentUnits = { unitIds: number[]; uniqueId: string };
export type PartialOffRentQuantities = { quantity: number; uniqueId: string };

export const PartialOffRentStep = ({
  order,
  setOrder,
  customPrices,
  configs,
  locations,
  offRentRequest,
  setOffRentRequest,
  inventoryReturnRequest,
  setInventoryReturnRequest,
  onCancel,
  onComplete,
  fuelCharges,
  setFuelCharges,
  isCalculating,
}: {
  order: Order;
  setOrder: (order: Order) => void;
  customPrices?: RentalProductPrice[];
  configs: Dictionary<Configuration>;
  locations: Location[];
  offRentRequest: OffRentRequest;
  setOffRentRequest: React.Dispatch<React.SetStateAction<OffRentRequest | undefined>>;
  inventoryReturnRequest?: InventoryReturnRequest;
  setInventoryReturnRequest: React.Dispatch<React.SetStateAction<InventoryReturnRequest | undefined>>;
  onCancel: () => void;
  onComplete: () => void;
  fuelCharges: FuelCharge[];
  setFuelCharges: React.Dispatch<React.SetStateAction<FuelCharge[]>>;
  isCalculating?: boolean;
}) => {
  const { configuration } = useContext(TenantContext);
  const { calculateFinancialSummary } = useAPI();
  const { splitOrderProductsUpdate } = useOrderApiV1();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Use the hour overage management hook
  const {
    isCalculating: isHourOverageCalculating,
    updateOverageOverride,
    getOverageForUnit,
    getOverrideForUnit,
  } = useHourOverageManagement(inventoryReturnRequest, order);
  const [partialOffRentUnitIds, setPartialOffRentUnitIds] = useState<Dictionary<PartialOffRentUnits>>(
    Object.fromEntries(
      order.products
        .filter(
          (op) =>
            op.type === OrderType.Rental &&
            op.status === OrderReturnStatus.OnRent &&
            op.product.trackingType === ProductTrackingType.SerialUnits,
        )
        .map((op) => [op.id, { uniqueId: uuidv4(), unitIds: op.assignedUnits.map((au) => au.id) }]),
    ),
  );
  const [partialOffRentQuantities, setPartialOffRentQuantities] = useState<Dictionary<PartialOffRentQuantities>>(
    Object.fromEntries(
      order.products
        .filter(
          (op) =>
            op.type === OrderType.Rental &&
            op.status === OrderReturnStatus.OnRent &&
            op.product.trackingType === ProductTrackingType.Quantity,
        )
        .map((op) => [op.id, { uniqueId: uuidv4(), quantity: op.quantity }]),
    ),
  );
  const currency = configuration ? (configuration.currency as Dinero.Currency) : undefined;
  const unitTrackingType = configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber';
  const showInventoryReturn = configs['CONF_ALLOW_COMBINED_OFF_RENT_RETURN']?.value === '1';

  // Make sure the off rent quantity is not greater than any of the order product quantities
  const isValid =
    offRentRequest &&
    offRentRequest.offRentProducts.map((orp) => orp.productId).length > 0 &&
    offRentRequest.offRentProducts.every((orp) => {
      const op = order.products.find((op) => op.id === orp.productId);

      return orp.quantity === undefined || (op ? op.quantity >= orp.quantity : false);
    });

  useEffect(() => {
    // toggle off rent to be off if all units unselected
    Object.entries(partialOffRentUnitIds).map(([opId, v]) => {
      if (!v.unitIds.length && offRentRequest.offRentProducts.map((orp) => orp.productId).includes(parseInt(opId))) {
        onToggleOffRent(order.products.find((op) => op.id === parseInt(opId))!);
        setPartialOffRentUnitIds({
          ...partialOffRentUnitIds,
          [opId]: {
            ...partialOffRentUnitIds[opId],
            unitIds: order.products.find((op) => op.id === parseInt(opId))?.assignedUnits.map((au) => au.id) ?? [],
          },
        });
      }
    });
  }, [partialOffRentUnitIds]);

  const isProductPartialOffRent = (opId: number): boolean => {
    return (
      offRentRequest.offRentProducts.map((orp) => orp.productId).some((id) => id === opId) &&
      partialOffRentUnitIds[opId] &&
      partialOffRentUnitIds[opId].unitIds.length > 0 &&
      !isEqual(
        partialOffRentUnitIds[opId].unitIds,
        order.products.find((op) => op.id === opId)?.assignedUnits.map((au) => au.id),
      )
    );
  };

  const isProductPartialOffRentQuantity = (opId: number): boolean => {
    return (
      offRentRequest.offRentProducts.map((orp) => orp.productId).some((id) => id === opId) &&
      partialOffRentQuantities[opId] &&
      partialOffRentQuantities[opId].quantity > 0 &&
      partialOffRentQuantities[opId].quantity !== order.products.find((op) => op.id === opId)?.quantity
    );
  };

  const next = async () => {
    try {
      setIsLoading(true);

      const anyPartialOffRentUnitOrQuantity =
        Object.keys(partialOffRentUnitIds).some((opId) => isProductPartialOffRent(parseInt(opId))) ||
        Object.keys(partialOffRentQuantities).some((opId) => isProductPartialOffRentQuantity(parseInt(opId)));

      if (order && anyPartialOffRentUnitOrQuantity) {
        const cfsReq = orderToCalculateFinancialSummaryRequest(
          order,
          customPrices ?? [],
          partialOffRentUnitIds,
          isProductPartialOffRent,
          partialOffRentQuantities,
          isProductPartialOffRentQuantity,
        );
        const updatedSummary = await calculateFinancialSummary(cfsReq);
        const partialOffRentRequest = toPartialOffRentRequest(
          order,
          customPrices ?? [],
          updatedSummary,
          offRentRequest,
          currency ?? (configs['CONF_CURRENCY']?.value as Dinero.Currency),
          partialOffRentUnitIds,
          isProductPartialOffRent,
          partialOffRentQuantities,
          isProductPartialOffRentQuantity,
        );
        const updatedOrder = await splitOrderProductsUpdate(order.id, partialOffRentRequest);
        const newSplitOrderProducts = updatedOrder.products.filter((op) => {
          return !order.products.some((ogOp) => op.id == ogOp.id);
        });
        setOffRentRequest((prev) => {
          return (
            prev && {
              ...prev,
              offRentProducts: [
                ...prev.offRentProducts.filter(
                  (orp) => !isProductPartialOffRent(orp.productId) && !isProductPartialOffRentQuantity(orp.productId),
                ),
                ...newSplitOrderProducts.map((op) => ({ productId: op.id, type: op.product.trackingType })),
              ],
            }
          );
        });

        setInventoryReturnRequest((prev) => {
          const opIds = prev && [
            // Non partial, just full ones
            ...prev.orderProductIds.filter(
              (opId) => !isProductPartialOffRent(opId) && !isProductPartialOffRentQuantity(opId),
            ),
            ...newSplitOrderProducts
              .filter(
                (nop) =>
                  nop.trackingType === ProductTrackingType.SerialUnits &&
                  prev.orderProductIds.find((opId) => {
                    return nop.assignedUnits
                      .map((u) => u.id)
                      .every((id) => partialOffRentUnitIds[opId.toString()]?.unitIds.includes(id));
                  }),
              )
              .map((nop) => nop.id),
            ...newSplitOrderProducts
              .filter((nop) => nop.trackingType === ProductTrackingType.Quantity)
              .map((nop) => nop.id),
          ];
          return (
            prev && {
              ...prev,
              orderProductIds: opIds ?? [],
              hourReadings: prev.hourReadings.filter((hr) =>
                opIds!.includes(
                  updatedOrder.products.find((op) => op.assignedUnits.find((au) => au.id === hr.unitId))?.id ?? 0,
                ),
              ),
            }
          );
        });
        setFuelCharges((prev) => {
          return (prev || []).map((fc) => {
            const op = updatedOrder.products.find((op) => op.assignedUnits.find((au) => au.id === fc.unitId));
            return { ...fc, orderProductId: op?.id ?? fc.orderProductId }; // update orderProductIds for units in case partial off rent
          });
        });

        setOrder(updatedOrder);
        onComplete();
      } else {
        onComplete();
      }
    } catch (err: any) {
      console.error(err);
      toast(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const onToggleOffRent = (op: OrderProduct) => {
    const isSelected = offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id);
    setOffRentRequest((prev) => {
      return (
        prev && {
          ...prev,
          offRentProducts: [
            ...prev.offRentProducts.filter((orp) => orp.productId !== op.id),
            ...(isSelected ? [] : [{ productId: op.id, type: op.product.trackingType, quantity: op.quantity }]),
          ],
        }
      );
    });
    if (op.product.trackingType === ProductTrackingType.SerialUnits) {
      setPartialOffRentUnitIds((partialOffRentUnitIds) => ({
        ...partialOffRentUnitIds,
        [op.id]: {
          ...partialOffRentUnitIds[op.id],
          unitIds: partialOffRentUnitIds[op.id]?.unitIds ?? op.assignedUnits.map((au) => au.id),
        },
      }));
    }

    if (op.product.trackingType === ProductTrackingType.Quantity) {
      setPartialOffRentQuantities((prev) => {
        if (isSelected) {
          const { [op.id]: _, ...rest } = prev;
          return rest;
        } else {
          return {
            ...prev,
            [op.id]: {
              quantity: op.quantity,
              uniqueId: uuidv4(),
            },
          };
        }
      });
    }

    if (isSelected) {
      setInventoryReturnRequest((prev) => {
        return (
          prev && {
            ...prev,
            orderProductIds: prev.orderProductIds.filter((opId) => opId !== op.id),
          }
        );
      });
      setFuelCharges((prev) => {
        return (prev || []).filter((fc) => fc.orderProductId !== op.id);
      });
    }
  };

  const onToggleInventoryReturn = (op: OrderProduct) => {
    const isSelectedForReturn = inventoryReturnRequest?.orderProductIds.includes(op.id);
    setInventoryReturnRequest((prev) => {
      return (
        prev && {
          ...prev,
          orderProductIds: [
            ...prev.orderProductIds.filter((opId) => opId !== op.id),
            ...(isSelectedForReturn ? [] : [op.id]),
          ],
        }
      );
    });
    if (isSelectedForReturn) {
      setFuelCharges((prev) => {
        return (prev || []).filter((fc) => fc.orderProductId !== op.id);
      });
    }
  };

  return (
    <div className="confirm-off-rent">
      <div className="flex flex-column w-full gap-2">
        {showInventoryReturn && inventoryReturnRequest && locations?.length > 1 && (
          <InventoryReturnLocation
            locations={locations}
            order={order}
            returnToLocation={locations.find((l) => inventoryReturnRequest.returnToLocation === l.id)!}
            setReturnToLocation={(l) =>
              setInventoryReturnRequest((prev) => {
                return { ...prev!, returnToLocation: l.id };
              })
            }
            disabled={inventoryReturnRequest.orderProductIds.length === 0}
          />
        )}
        <div>
          <div className={`grid grid-cols-4 gap-4 text-[0.9rem] off-rent-table mb-8`}>
            {/* -------- */}
            <div className={showInventoryReturn ? 'col-span-2' : 'col-span-4'}>Products</div>
            {showInventoryReturn && (
              <div className="col-span-2">
                <label>Return Inventory</label>
              </div>
            )}
            {/* ---------- row break --------- */}
            {order.products
              .filter((op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent)
              .map((op) => {
                const isSelected = offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id);
                const isSelectedForReturn = inventoryReturnRequest?.orderProductIds.includes(op.id);

                return (
                  <>
                    {/* ---------- row break --------- */}
                    <div
                      key={`${op.id}-toggle`}
                      className={`flex items-center gap-2 ${!showInventoryReturn ? 'col-span-4' : 'col-span-2'}`}
                    >
                      <div>
                        <Toggle checked={isSelected} onClick={() => onToggleOffRent(op)} />
                      </div>
                      <div>{op.product.type === ProductType.Custom ? op.name : op.product.name}</div>
                    </div>
                    {showInventoryReturn && (
                      <div className="col-span-2" key={`${op.id}-return`}>
                        <Toggle checked={isSelectedForReturn} onClick={() => onToggleInventoryReturn(op)} />
                      </div>
                    )}

                    {/* ---------- row break --------- */}
                    {isSelected && op.product.trackingType === ProductTrackingType.Quantity && (
                      <QuantityOffRentInput
                        orderProduct={op}
                        setOffRentRequest={setOffRentRequest}
                        offRentRequest={offRentRequest}
                        setPartialOffRentQuantities={setPartialOffRentQuantities}
                      />
                    )}
                    {isSelected && op.product.trackingType === ProductTrackingType.SerialUnits && (
                      <>
                        <div className={`ml-8 col-span-2`}>Unit</div>
                        <div className="col-span-1">
                          {isSelectedForReturn &&
                            inventoryReturnRequest &&
                            op.product.trackingType === ProductTrackingType.SerialUnits &&
                            op.product.allowHourTracking && <label>Hours In</label>}
                        </div>

                        <div className="col-span-1">
                          {isSelectedForReturn && inventoryReturnRequest && op.product.fuelChargeProductId && (
                            <div className="flex-1">
                              Fuel ({(configs['CONF_FUEL_UNIT']?.value ?? 'Gallons') as 'Gallons' | 'Liters'})
                            </div>
                          )}
                        </div>

                        {/* ---------- row break --------- */}

                        {op.assignedUnits.flatMap((au) => {
                          const isUnitChecked = (partialOffRentUnitIds[op.id]?.unitIds ?? []).includes(au.id);
                          return (
                            <>
                              <div className={`flex gap-3 items-center ml-8 col-span-2`}>
                                <Checkbox
                                  isChecked={isUnitChecked}
                                  onChange={(v) =>
                                    setPartialOffRentUnitIds({
                                      ...partialOffRentUnitIds,
                                      [op.id]: {
                                        ...partialOffRentUnitIds[op.id],
                                        unitIds: v
                                          ? [...(partialOffRentUnitIds[op.id]?.unitIds ?? []), au.id]
                                          : (partialOffRentUnitIds[op.id]?.unitIds ?? []).filter((id) => id !== au.id),
                                      },
                                    })
                                  }
                                />
                                <div>{unitTrackingType === 'serialNumber' ? au.serialNumber : au.unitNumber}</div>
                              </div>

                              <div className="col-span-1">
                                {isSelectedForReturn && inventoryReturnRequest && isUnitChecked && (
                                  <InventoryReturnHourInput
                                    readingsDisabled={false}
                                    key={au.id}
                                    unitTrackingType={unitTrackingType}
                                    showUnit={false}
                                    unit={au}
                                    orderProduct={op}
                                    hourReading={
                                      inventoryReturnRequest.hourReadings.find((hr) => hr.unitId === au.id)?.hourReading
                                    }
                                    setHourReading={(hr) =>
                                      setInventoryReturnRequest((r) => {
                                        return r
                                          ? {
                                              ...r,
                                              hourReadings: [
                                                ...r.hourReadings.filter((hr) => hr.unitId !== au.id),
                                                { unitId: au.id, hourReading: hr },
                                              ],
                                            }
                                          : undefined;
                                      })
                                    }
                                    overage={getOverageForUnit(op.id, au.id)}
                                    overageOverride={getOverrideForUnit(op.id, au.id)}
                                    setOverageOverride={(value) => updateOverageOverride(op.id, au.id, value)}
                                    isCalculating={isHourOverageCalculating}
                                  />
                                )}
                              </div>

                              <div className="flex col-span-1 gap-4 items-baseline">
                                {isSelectedForReturn &&
                                  inventoryReturnRequest &&
                                  isUnitChecked &&
                                  op.product.fuelChargeProductId && (
                                    <InventoryReturnFuelChargeInput
                                      orderProduct={op}
                                      fuelAmount={fuelCharges.find((fc) => fc.unitId === au.id)?.amount}
                                      setFuelAmount={(fuelAmount) =>
                                        setFuelCharges((r) => {
                                          const existing = r?.find(
                                            (fc) => fc.orderProductId === op.id && fc.unitId === au.id,
                                          );

                                          return [
                                            ...(r || []).filter(
                                              (fc) =>
                                                fc.orderProductId !== op.id ||
                                                (fc.orderProductId === op.id && fc.unitId !== au.id),
                                            ),
                                            {
                                              ...existing,
                                              orderProductId: op.id,
                                              fuelChargeProductId: op.product.fuelChargeProductId,
                                              unitId: au.id,
                                              amount: fuelAmount,
                                              uuid: existing?.uuid ?? uuidv4() + 'fuel',
                                            },
                                          ];
                                        })
                                      }
                                    />
                                  )}
                              </div>
                            </>
                          );
                        })}
                      </>
                    )}
                  </>
                );
              })}
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-3 w-full">
        <Button className="button button-secondary w-full" onClick={() => onCancel()}>
          Cancel
        </Button>
        <Button
          className="button button-yellow w-full"
          disabled={!isValid || isLoading || isCalculating}
          onClick={() => next()}
        >
          {isCalculating ? (
            <div className="flex items-center gap-2">
              <Spinner />
              Calculating...
            </div>
          ) : (
            'Next'
          )}
        </Button>
      </div>
    </div>
  );
};
